import { DocumentType } from "@1games-ru/region-shared/dist/enums/documents/documents";
import { DocumentData } from "@1games-ru/region-shared/dist/types/documents/documentData";
import { merge } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import AdminEmployee from "@/shared/widgets/documents/AdminEmployee";
import CriminalRecordCertificate from "@/shared/widgets/documents/CriminalRecordCertificate";
import DefenseMinistryEmployee from "@/shared/widgets/documents/DefenseMinistryEmployee";
import DriverLicense from "@/shared/widgets/documents/DriverLicense";
import FSBEmployee from "@/shared/widgets/documents/FSBEmployee";
import HospitalEmployee from "@/shared/widgets/documents/HospitalEmployee";
import MedicalCard from "@/shared/widgets/documents/MedicalCard";
import MilitaryID from "@/shared/widgets/documents/MilitaryID";
import Passport from "@/shared/widgets/documents/Passport";
import PoliceEmployee from "@/shared/widgets/documents/PoliceEmployee";
import PsychiatricCertificate from "@/shared/widgets/documents/PsychiatricCertificate";
import TrafficPoliceEmployee from "@/shared/widgets/documents/TrafficPoliceEmployee";
import VehicleRegistrationCertificate from "@/shared/widgets/documents/VehicleRegistrationCertificate";
import WeaponLicense from "@/shared/widgets/documents/WeaponLicense";
import useShowDocuments from "@/domains/documents/hooks/useShowDocuments";
import useShowSignatureModal from "@/domains/documents/hooks/useShowSignature";
import useUploadSignature from "@/domains/documents/hooks/useUploadSignature";
import SigModal from "@/domains/documents/components/SigModal";

const componentsDict: Record<DocumentType, FC> = {
  [DocumentType.AdminEmployee]: AdminEmployee,
  [DocumentType.HospitalEmployee]: HospitalEmployee,
  [DocumentType.CriminalRecordCertificate]: CriminalRecordCertificate,
  [DocumentType.DriverLicense]: DriverLicense,
  [DocumentType.FSBEmployee]: FSBEmployee,
  [DocumentType.TrafficPoliceEmployee]: TrafficPoliceEmployee,
  [DocumentType.MedicalCard]: MedicalCard,
  [DocumentType.MilitaryID]: MilitaryID,
  [DocumentType.DefenseMinistryEmployee]: DefenseMinistryEmployee,
  [DocumentType.PoliceEmployee]: PoliceEmployee,
  [DocumentType.PsychiatricCertificate]: PsychiatricCertificate,
  [DocumentType.VehicleRegistrationCertificate]: VehicleRegistrationCertificate,
  [DocumentType.WeaponLicense]: WeaponLicense,
  [DocumentType.Passport]: Passport,
};

export default function DocumentsContainer() {
  const { document } = useShowDocuments();
  const [sig, setSig] = useState("");
  const [uploadingSig, setUploadingSig] = useState(false);
  const { uploadSignature } = useUploadSignature();
  const { isShowSignatureModal, sigToken, setIsShowSignatureModal } = useShowSignatureModal();

  const Component = document ? componentsDict[document.type] : null;

  const propsForComponent: DocumentData | null = useMemo(() => {
    if (!document) return null;

    return merge(document, {
      // owner: {
      //   photo: getPhotoUrl(document.data.docNumber, document.data.owner.photo),
      //   signature: getSignatureUrl(document.data.docNumber, document.data.owner.signature),
      // },
    });
  }, [document, sig]);

  useEffect(() => {
    if (document) {
      setSig(document.owner?.signature || "");
    }
  }, [document]);

  const onChangeSig = useCallback(
    async ({ sigUrl, sigBlob }: { sigUrl: string; sigBlob: Blob }) => {
      setUploadingSig(true);
      const response = await uploadSignature(sigBlob, sigToken);
      if (response) {
        setSig(sigUrl);
        await RpcMpProvider.callServer("CreatePassport", {});
      }
      setUploadingSig(false);
    },
    [uploadSignature, sigToken],
  );

  return (
    <>
      {isShowSignatureModal && (
        <SigModal
          open
          onClose={() => {
            setIsShowSignatureModal(false);
            GameMpProvider.closeCurrentInterface();
          }}
          onChangeSig={onChangeSig}
          pending={uploadingSig}
        />
      )}
      {propsForComponent && (
        <div className="fixed inset-0 px-[--boundary-offset-x] py-[--boundary-offset-y] flex items-center justify-center">
          <Component {...(propsForComponent as React.ComponentProps<typeof Component>)} />
        </div>
      )}
    </>
  );
}
